import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './index.css';
import SEO from '../../seo';
import { PAGE_SEO, COMMON_KEYWORDS } from '../../seo/pageSeo';

const models = [
  {
    label: 'X5-160',
    width: '1600mm',
    specs: [
      { label: '最大面料重量', value: '80kg' },
      { label: '最大驱动速度', value: '88m/min' },
      { label: '功率', value: '1.2KW' },
      { label: '电源', value: '220V / 15A' },
    ],
  },
  {
    label: 'X5-190',
    width: '1900mm',
    specs: [
      { label: '最大面料重量', value: '80kg' },
      { label: '最大驱动速度', value: '88m/min' },
      { label: '功率', value: '1.2KW' },
      { label: '电源', value: '220V / 15A' },
    ],
  },
  {
    label: 'X5-210',
    width: '2100mm',
    specs: [
      { label: '最大面料重量', value: '80kg' },
      { label: '最大驱动速度', value: '88m/min' },
      { label: '功率', value: '1.2KW' },
      { label: '电源', value: '220V / 15A' },
    ],
  },
  {
    label: 'X5-260',
    width: '2600mm',
    specs: [
      { label: '最大面料重量', value: '110kg' },
      { label: '最大驱动速度', value: '85m/min' },
      { label: '功率', value: '1.55KW' },
      { label: '电源', value: '220V / 15A' },
    ],
  },
  {
    label: 'X5-310',
    width: '3100mm',
    specs: [
      { label: '最大面料重量', value: '110kg' },
      { label: '最大驱动速度', value: '85m/min' },
      { label: '功率', value: '1.55KW' },
      { label: '电源', value: '220V / 15A' },
    ],
  },
];

const modules = [
  {
    num: '01',
    name: '大布斗',
    nameEn: 'Large Cloth Hopper',
    nameVi: 'Khay vải lớn',
    description:
      '大容量布斗设计，可存放大量布料，大幅提升工作效率，支持连续生产。深V槽结构确保布料张力充分释放，松紧适度。',
    descriptionEn:
      'Large-capacity cloth hopper stores abundant fabric, dramatically improving work efficiency and supporting continuous production. Deep-V slot structure ensures full tension release.',
    descriptionVi:
      'Khay vải dung tích lớn chứa nhiều vải, cải thiện đáng kể hiệu quả công việc và hỗ trợ sản xuất liên tục. Cấu trúc rãnh V sâu đảm bảo giải phóng lực căng đầy đủ.',
    imageUrl: 'https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/zm_hero.webp',
    align: 'left',
  },
  {
    num: '02',
    name: '二次剥离',
    nameEn: 'Double Stripping',
    nameVi: 'Bóc tách hai lớp',
    description:
      '微电脑自动计算，光电传感自动解布送布。确保每一次布料的铺设都准确无误，优化布料处理过程，减少废料。',
    descriptionEn:
      'Microcomputer automatic calculation with photoelectric sensor for automatic cloth delivery. Ensures precise fabric spreading every time, optimizing processing and reducing waste.',
    descriptionVi:
      'Tính toán tự động bằng vi tính với cảm biến quang điện tự động cấp vải. Đảm bảo trải vải chính xác mỗi lần, tối ưu hóa quy trình và giảm phế liệu.',
    imageUrl: 'https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/x6_module4.webp',
    align: 'right',
  },
  {
    num: '03',
    name: '控制中心',
    nameEn: 'Control Center',
    nameVi: 'Trung tâm điều khiển',
    description:
      '先进的人机交互界面，操作简单直观。允许操作者轻松调整机器设置以适应不同的生产线需求，参数实时监控。',
    descriptionEn:
      'Advanced human-machine interface, simple and intuitive operation. Allows operators to easily adjust machine settings for different production line requirements.',
    descriptionVi:
      'Giao diện người-máy tiên tiến, vận hành đơn giản và trực quan. Cho phép người vận hành dễ dàng điều chỉnh cài đặt máy cho các yêu cầu dây chuyền sản xuất khác nhau.',
    imageUrl: 'https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/x6_module5.webp',
    align: 'left',
  },
  {
    num: '04',
    name: '伺服驱动',
    nameEn: 'Servo Drive System',
    nameVi: 'Hệ thống truyền động servo',
    description:
      '伺服电机控制，低速时也能输出大力矩，定位精度可达微米级。电子同步实时跟随，送布张力均匀稳定。',
    descriptionEn:
      'Servomotor control outputs high torque at low speed with micron-level positioning accuracy. Electronic synchronization for uniform and stable feed tension.',
    descriptionVi:
      'Điều khiển động cơ servo xuất mô-men xoắn cao ở tốc độ thấp với độ chính xác vị trí ở cấp độ micron. Đồng bộ điện tử cho lực căng cấp đồng đều và ổn định.',
    imageUrl: 'https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/x6_module2.webp',
    align: 'right',
  },
];

function X5ModelCard({ model, index, t }) {
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="x5-model-card"
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      <div className="x5-model-card__label">{model.label}</div>
      <div className="x5-model-card__width">{model.width}</div>
      <div className="x5-model-card__divider" />
      <ul className="x5-model-card__specs">
        {model.specs.map((spec, i) => (
          <li key={i}>
            <strong>{spec.value}</strong>
            {t(spec.labelKey)}
          </li>
        ))}
      </ul>
    </div>
  );
}

function X5ModuleItem({ module, index, getModuleText }) {
  const itemRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.15 }
    );

    if (itemRef.current) {
      observer.observe(itemRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const moduleName = getModuleText(module, 'name');
  const moduleDesc = getModuleText(module, 'description');

  return (
    <div
      ref={itemRef}
      className={`x5-module-item x5-module-item--${module.align}`}
      style={{ transitionDelay: `${index * 100}ms` }}
    >
      <div className="x5-module-item__media">
        <img
          src={module.imageUrl}
          alt={moduleName}
          className="x5-module-item__img"
          loading="lazy"
        />
        <div className="x5-module-item__badge">
          <span>{module.num}</span>
        </div>
      </div>

      <div className="x5-module-item__body">
        <div className="x5-module-item__num">
          {module.num} / {modules.length.toString().padStart(2, '0')}
        </div>
        <h3 className="x5-module-item__name">
          {moduleName}
          <br />
          <span style={{ fontSize: '0.55em', color: 'var(--color-primary)', fontWeight: 700 }}>
            {getModuleText(module, 'nameEn')}
          </span>
        </h3>
        <div className="x5-module-item__divider" />
        <p className="x5-module-item__desc">{moduleDesc}</p>
      </div>
    </div>
  );
}

export default function X5Machine() {
  const { t, i18n } = useTranslation();

  const getModuleText = (module, field) => {
    const lang = i18n.language;
    if (lang.startsWith('vi')) return module[`${field}Vi`] || module[`${field}`] || module[`${field}En`];
    if (lang.startsWith('en')) return module[`${field}En`] || module[`${field}`];
    return module[field];
  };

  const getSpecLabelKey = (index) => {
    const keys = ['product_max_fabric_weight', 'product_max_drive_speed', 'product_power', 'product_power_source'];
    return keys[index] || 'product_max_fabric_weight';
  };

  const modelsWithSpecKeys = models.map(m => ({
    ...m,
    specs: m.specs.map((s, i) => ({ ...s, labelKey: getSpecLabelKey(i) }))
  }));

  return (
    <>
    <SEO
        pageKey="x5"
        title={PAGE_SEO.x5.title}
        description={PAGE_SEO.x5.description}
        keywords={COMMON_KEYWORDS}
        slug={PAGE_SEO.x5.slug}
    />
    <div className="x5-page">
      {/* Hero */}
      <div className="x5-hero">
        <img
          className="x5-hero__img"
          src="https://oss.xiaomaizn.com/xiaomai/xiaomai官网/files/images/products/x6_module3.webp"
          alt="X5 Series"
        />
        <div className="x5-hero__overlay" />
        <div className="x5-hero__content">
          <div className="x5-hero__eyebrow">
            {t('x5_hero_eyebrow')}
          </div>
          <h1 className="x5-hero__title">{t('x5_hero_title')}</h1>
          <p className="x5-hero__sub">
            {t('x5_hero_sub')}
          </p>
          <div className="x5-hero__rule" />
        </div>
      </div>

      {/* Specs Section */}
      <section className="x5-specs-section">
        <div className="x5-section-header">
          <div className="x5-section-eyebrow">
            {t('x5_specs_eyebrow')}
          </div>
          <h2 className="x5-section-title">
            {t('x5_specs_title')}
          </h2>
          <div className="x5-section-rule" />
        </div>

        <div className="x5-model-cards">
          {modelsWithSpecKeys.map((model, index) => (
            <X5ModelCard key={model.label} model={model} index={index} t={t} />
          ))}
        </div>
      </section>

      {/* Module Showcase */}
      <section className="x5-modules-section">
        <div className="x5-section-header">
          <div className="x5-section-eyebrow">
            {t('x5_modules_eyebrow')}
          </div>
          <h2 className="x5-section-title">
            {t('x5_modules_title')}
          </h2>
          <div className="x5-section-rule" />
        </div>

        {modules.map((module, index) => (
          <X5ModuleItem key={module.num} module={module} index={index} getModuleText={getModuleText} />
        ))}
      </section>

      {/* CTA */}
      <div className="x5-cta">
        <div className="x5-cta__inner">
          <p className="x5-cta__text">
            {t('x5_cta_text')}
            <br />
            <span className="x5-cta__accent">
              {t('x5_cta_accent')}
            </span>
          </p>
          <p className="x5-cta__sub">
            {t('x5_cta_sub')}
          </p>
        </div>
      </div>
    </div>
    </>
  );
}
